import { D2CCompanyProfileDto, Nullable } from '@orcar/common';
import {
  getDriverLicenseText,
  getDrivingExperienceText,
  Icon,
  isPossibleToText,
  RenewalCarFrontIcon,
  RenewalFishingIcon,
  RenewalForeignIcon,
  RenewalIdCardIcon,
  RenewalLicenseIcon,
  RenewalPetIcon,
  RenewalSoldierIcon,
  Styles,
} from '@orcar/common-d2c';
import { MutableRefObject } from 'react';
import styled from 'styled-components';

interface RentConditionProps {
  company?: D2CCompanyProfileDto;
  numberOfSeat?: Nullable<number>;
  driverAge?: Nullable<number>;
  drivingExperience?: Nullable<number>;
  hasPadding?: boolean;
  isMobile: boolean;
  tabRef?: MutableRefObject<Nullable<HTMLDivElement>[]>;
  unavailableItemTextColor?: string;
  unavailableItemIconColor?: Record<string, string>;
}

const RENTAL_CONDITION = {
  AGE: 'AGE',
  DRIVING_EXPERIENCE: 'DRIVING_EXPERIENCE',
  LICENSE: 'LICENSE',
  PET: 'PET',
  FISHING: 'FISHING',
  SOLDIER: 'SOLDIER',
  FOREIGN: 'FOREIGN',
};

const RentalCondition = ({
  isMobile,
  company,
  numberOfSeat,
  driverAge,
  drivingExperience,
  hasPadding,
  tabRef,
  unavailableItemTextColor,
  unavailableItemIconColor,
}: RentConditionProps) => {
  const CONDITIONS = [
    {
      id: RENTAL_CONDITION.AGE,
      icon: RenewalIdCardIcon,
      text: `만 ${driverAge}세 이상`,
    },
    {
      id: RENTAL_CONDITION.DRIVING_EXPERIENCE,
      icon: RenewalCarFrontIcon,
      text: `운전 경력 ${getDrivingExperienceText(drivingExperience)}`,
    },
    {
      id: RENTAL_CONDITION.LICENSE,
      icon: RenewalLicenseIcon,
      text: `면허 ${getDriverLicenseText(numberOfSeat)}`,
    },
    {
      id: RENTAL_CONDITION.PET,
      icon: RenewalPetIcon,
      text: `반려동물 동반 - ${isPossibleToText(
        company?.isAvailableWithPet as boolean,
      )}`,
      availability: company?.isAvailableWithPet,
    },
    {
      id: RENTAL_CONDITION.FISHING,
      icon: RenewalFishingIcon,
      text: `낚시 용품 지참 - ${isPossibleToText(
        company?.isAvailableWithFishingGear as boolean,
      )}`,
      availability: company?.isAvailableWithFishingGear,
    },
    {
      id: RENTAL_CONDITION.SOLDIER,
      icon: RenewalSoldierIcon,
      text: `군인 대여 - ${isPossibleToText(
        company?.isAvailableForSoldier as boolean,
      )}`,
      availability: company?.isAvailableForSoldier,
    },
    {
      id: RENTAL_CONDITION.FOREIGN,
      icon: RenewalForeignIcon,
      text: `외국인 대여 - 조건부 ${isPossibleToText(
        company?.isAvailableForForeigner as boolean,
      )}(하단 내용 확인)`,
      availability: company?.isAvailableForForeigner,
    },
  ];
  return (
    <ContentWrapper
      ref={(ref) => (tabRef ? (tabRef.current[0] = ref) : null)}
      isMobile={isMobile}
      hasPadding={hasPadding}
    >
      <SectionTitle isMobile={isMobile}>대여 조건</SectionTitle>
      <ConditionWrapper isMobile={isMobile}>
        {CONDITIONS.map((item) => (
          <LineWrapper
            key={item.id}
            isMobile={isMobile}
            color={
              item.availability === undefined || !!item.availability
                ? Styles.COLOR.GREY_01
                : unavailableItemTextColor || Styles.COLOR.GREY_03
            }
          >
            <Icon
              src={item.icon}
              width={20}
              height={20}
              iconcolor={
                !!item.availability || item.availability === undefined
                  ? Styles.SVG_FILTER.GREY_01
                  : unavailableItemIconColor || Styles.SVG_FILTER.GREY_03
              }
            />
            <p>{item.text}</p>
          </LineWrapper>
        ))}
      </ConditionWrapper>
    </ContentWrapper>
  );
};

export { RentalCondition };

const ContentWrapper = styled.div<{
  hasPadding?: boolean;
  isMobile?: boolean;
}>`
  width: 100%;
  padding: ${({ hasPadding }) => hasPadding && '30px 0'};
  scroll-margin: ${({ isMobile }) => (isMobile ? '110px' : '150px')};
`;

export const SectionTitle = styled.p<{
  isMobile: boolean;
  noMargin?: boolean;
  fontSize?: string;
  fontWeight?: number;
}>`
  margin-bottom: ${({ noMargin }) => !noMargin && '20px'};
  ${({ isMobile, theme }) => (isMobile ? theme.FONT.H4 : theme.FONT.H5)};

  font-size: ${({ fontSize }) => fontSize && fontSize};
  font-weight: ${({ fontWeight }) => fontWeight && fontWeight};
`;

const ConditionWrapper = styled.div<{ isMobile: boolean }>`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ isMobile }) => (isMobile ? '19px 20px' : '17px 60px')};
`;

const LineWrapper = styled.div<{ isMobile: boolean; color?: string }>`
  display: flex;
  gap: 0 10px;

  & > p {
    width: ${({ isMobile }) => (isMobile ? '120px' : '144px')};
    ${({ isMobile, theme }) => (isMobile ? theme.FONT.B2 : theme.FONT.B1)};
    color: ${({ theme, color }) => color || theme.COLOR.GREY_03};
  }
`;
